import React, { useCallback } from 'react';
import { shallowEqual } from 'react-redux';
import { Box, Divider, Typography } from '@mui/material';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import CanvasCardFrame, { CanvasCardDragSharedProps } from './CanvasCardFrame';
import { REFERENCE_CARD_SX } from './CanvasReferenceCard';
import { useAppSelector } from '../hooks';
import { RootState } from '../store';
import { selectAllRepositories, selectAllTimelineCollections } from '../containers/contentSlice';

export type CanvasTimelineCardProps = CanvasCardDragSharedProps & {
	timelineId: string;
	name: string;
};

function CanvasTimelineCardBase({
	position,
	isSelected,
	scale,
	collectionColor,
	isPositionLocked,
	onPositionChange,
	constrainPosition,
	onDragStart,
	onDragActivate,
	onDragMove,
	onDragEnd,
	cardNodeRef,
	onFocus,
	onBlur,
	onSelect,
	timelineId,
	name,
}: CanvasTimelineCardProps) {
	const selectCollectionRepositories = useCallback(
		(state: RootState) => {
			const repositoryIds = selectAllTimelineCollections(state)
				.filter((collection) => collection.timelineId === timelineId)
				.map((collection) => collection.repositoryId);
			const allRepositories = selectAllRepositories(state);
			return repositoryIds.map((repositoryId) => allRepositories.find((r) => r.id === repositoryId));
		},
		[timelineId],
	);
	const repositories = useAppSelector(selectCollectionRepositories, shallowEqual);
	return (
		<CanvasCardFrame
			position={position}
			scale={scale}
			isSelected={isSelected}
			collectionColor={collectionColor}
			isPositionLocked={isPositionLocked}
			onPositionChange={onPositionChange}
			constrainPosition={constrainPosition}
			onDragStart={onDragStart}
			onDragActivate={onDragActivate}
			onDragMove={onDragMove}
			onDragEnd={onDragEnd}
			cardNodeRef={cardNodeRef}
			onFocus={onFocus}
			onBlur={onBlur}
			onSelect={onSelect}
			ariaLabel={'Timeline'}
			maxWidth
			sx={REFERENCE_CARD_SX}
			zIndex={0}
		>
			<Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 0 }}>
				<Box display="flex" alignItems="center" gap={1} mt={0.25}>
					<ViewTimelineIcon sx={{ color: 'text.secondary' }} />
					<Typography variant="h6" color="text.primary" sx={{ overflow: 'hidden', wordBreak: 'break-word' }}>
						{name}
					</Typography>
				</Box>
				{repositories.length > 1 && <Divider />}
				{repositories.length > 1 && repositories.map((repository, index) => (
					<Box key={repository?.id ?? index} display="flex" alignItems="center" gap={0.75}>
						{repository?.color && <Box sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, backgroundColor: repository.color }} />}
						<Typography variant="body2" sx={{ color: 'text.primary' }}>
							{repository?.title ?? ''}
						</Typography>
					</Box>
				))}
			</Box>
		</CanvasCardFrame>
	);
}

/** Memoized so panning/zooming (which re-renders CanvasView every frame) doesn't also re-render every timeline card — CanvasView keeps this card's props referentially stable across those frames specifically so this bail-out holds. */
export const CanvasTimelineCard = React.memo(CanvasTimelineCardBase);
export default CanvasTimelineCard;
